/**
 * **确定性 JSON 规范化 + 哈希。**
 *
 * 用途只有一个：让客户端能回答"我这份视图和房主说的是同一份吗"。
 * 视图不一致时必须能发现（`docs/netcode-s3.md` §6），否则渲染层会拿一份
 * 悄悄错掉的状态继续画 —— 那种 bug 不会崩，只会很难查。
 *
 * ## 三条刻意的选择
 *
 * 1. **不引入 `object-hash`。** 那是个通用库，而我们只需要"把 JSON 排成
 *    确定的一串字节"。自己写 20 行，少一个依赖，而且**哈希的是什么**
 *    （视图，不是完整状态）完全由我们说了算。
 * 2. **必须递归排序对象键。** `{a:1,b:2}` 和 `{b:2,a:1}` 在 JS 里是两个不同的
 *    字符串，但它们是同一份数据。不排序就会出现"内容一样但哈希不同"的假警报，
 *    而假警报比不报警更糟 —— 它会让 `resync` 兜底路径变成常规路径。
 * 3. **FNV-1a 64 位，不是 SHA-256。** 本项目明确**不做任何安全设计**
 *    （`docs/archive/ans.md` Q3）：这个哈希用来抓"实现写错了"，不是用来防对手。
 *    选它是因为它**同步**（`crypto.subtle` 是异步的，会把整条路径染成 async）
 *    且无依赖。64 位对"抓 bug"这个目标绰绰有余。
 */

/** JSON 能表示的值。视图按设计必须落在这个集合里（`core/state.ts` 的硬约束 1） */
export type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };

/**
 * 规范化为**确定的一串文本**。
 *
 * 规则（与 `JSON.stringify` 的差异只有"键序"和"undefined"两条）：
 *
 * | 输入 | 输出 |
 * | --- | --- |
 * | 对象 | 键**按码位升序**，跳过值为 `undefined` 的键 |
 * | 数组 | 保序；洞与 `undefined` 变成 `null`（与 `JSON.stringify` 一致） |
 * | 数字 | `JSON.stringify` 的写法（`NaN` / `Infinity` → `null`） |
 * | `-0` | `0`（`JSON.stringify(-0)` 就是 `"0"`，保持一致） |
 * | 函数 / Symbol | 同 `undefined` |
 *
 * 不认识的类型（类实例、`Map`、`Set`）**不走特殊分支** —— 它们走到
 * 对象的默认分支，于是只序列化可枚举的自有属性。这是刻意的：
 * 与其在这里猜，不如让视图保持是纯 JSON（`core/` 已经保证了这一点）。
 */
export function canonicalJson(value: unknown): string {
  if (value === null) return 'null';
  switch (typeof value) {
    case 'boolean':
      return value ? 'true' : 'false';
    case 'number':
      return Object.is(value, -0) ? '0' : JSON.stringify(value);
    case 'string':
      return JSON.stringify(value);
    case 'bigint':
      // JSON 里没有 bigint。宁可写成字符串也不要抛 —— 抛出去会变成
      // 一条查不到源头的 500，而"多了一对引号"是看得见的
      return JSON.stringify(value.toString());
    case 'undefined':
    case 'function':
    case 'symbol':
      return 'null';
    default:
      break;
  }
  if (Array.isArray(value)) {
    let out = '[';
    for (let i = 0; i < value.length; i++) {
      if (i > 0) out += ',';
      out += canonicalJson(value[i]);
    }
    return `${out}]`;
  }
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record).sort();
  let out = '{';
  let first = true;
  for (const key of keys) {
    const item = record[key];
    if (item === undefined || typeof item === 'function' || typeof item === 'symbol') continue;
    if (!first) out += ',';
    first = false;
    out += `${JSON.stringify(key)}:${canonicalJson(item)}`;
  }
  return `${out}}`;
}

const FNV_OFFSET = 0xcbf29ce484222325n;
const FNV_PRIME = 0x100000001b3n;
const U64 = 0xffffffffffffffffn;

const encoder = new TextEncoder();

/**
 * FNV-1a，64 位，输出 16 位十六进制。
 *
 * 用 `BigInt` 而不是两个 32 位半字：宽度就是 64，不绕弯子。
 * 视图只有几 KB，逐字节的 BigInt 运算在这个规模上远低于一帧的预算。
 */
export function fnv1a64(text: string): string {
  let hash = FNV_OFFSET;
  const bytes = encoder.encode(text);
  for (let i = 0; i < bytes.length; i++) {
    hash ^= BigInt(bytes[i] as number);
    hash = (hash * FNV_PRIME) & U64;
  }
  return hash.toString(16).padStart(16, '0');
}

/** 规范化的文本 → 哈希。**网络上传的就是这个值** */
export function hashValue(value: unknown): string {
  return fnv1a64(canonicalJson(value));
}
